import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  Answer,
  EAnswerStatus,
  Expense,
  FileAnswer,
} from 'src/app/models/Answer.model';
import { AnswersStore } from 'src/app/store/answers-store';
import { combineLatest, debounceTime, map, skip, startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { Question } from 'src/app/models/question.model';
import { UploadFileLocalDisplayComponent } from '../../upload-file-display/upload-file-local-display/upload-file-local-display.component';
import { OptionsList } from 'src/app/models/optionsList.model';
import { QuestionnaireStore } from 'src/app/store/questionnaire-store';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { AnswerOptionItem } from 'src/app/models/answerOptionItem.model';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { DateQuestionDisplayComponent } from '../../date-question-display/date-question-display.component';
import {
  DualCalendarRangePickerComponent,
  ICalendarResults,
} from 'src/app/shared/components/dual-calendar-range-picker/dual-calendar-range-picker.component';
import { QueDatePipe } from 'src/app/pipes/date.pipe';
import { environment } from 'src/environment';
import { ToggleComponent } from 'src/app/shared/components/toggle/toggle.component';
@Component({
    selector: 'que-expense-item-display',
    imports: [
        CommonModule,
        UploadFileLocalDisplayComponent,
        ReactiveFormsModule,
        OverlayModule,
        TranslateDirective, TranslatePipe,
        SvgIconComponent,
        QueDatePipe,
        DualCalendarRangePickerComponent,
        ToggleComponent,
    ],
    templateUrl: './expense-item-display.component.html',
    styleUrls: ['./expense-item-display.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ExpenseItemDisplayComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: ExpenseItemDisplayComponent,
            multi: true,
        },
    ]
})
export class ExpenseItemDisplayComponent
  implements OnInit, ControlValueAccessor, Validator
{
  @Input() question?: Question;
  @Input() row?: number;
  @Input() canDelete?: boolean;
  @Output() deleteExpense = new EventEmitter();
  @Input() viewAnswer = false;
  @Input() isPreview = false;
  @ViewChild('trigger') trigger!: ElementRef;

  #builder = inject(FormBuilder);
  #answerStore = inject(AnswersStore);
  #questionnaireStore = inject(QuestionnaireStore);

  // Single source of truth shared with ExpenseDisplayComponent (the parent) -
  // see AnswersStore.canEditClientChargeFields$.
  canEditClientChargeFields = toSignal(
    this.#answerStore.canEditClientChargeFields$,
    { initialValue: false },
  );

  readonly positonAreaForSelectedDate = [
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'top',
    },
  ] as ConnectedPosition[];

  readonly positonArea = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ] as ConnectedPosition[];
  currentExpense?: Expense;
  today = new Date();
  selectedDateIsOpened: boolean = false;
  answer?: Answer;
  orginalAnswer?: Answer;
  isUser: boolean = false;
  isNotUser$ = this.#questionnaireStore.isUser$.pipe(map((x) => !x));
  // isSelectCurrencyOpen: boolean = false;
  isSelectTypeOpen: boolean = false;
  // selectedCurrency?: string;
  selectedTypeName?: string;
  typeName?: string;
  // currencyList: string[] = ['₪', '$', '€'];
  expense = this.#builder.group(
    {
      // id: new FormControl<number | null>(null),
      expenseType: new FormControl<AnswerOptionItem | null>(null, [
        Validators.required,
      ]),
      description: new FormControl<string | null>(null),
      documentNumber: new FormControl<string | null>(null, [
        Validators.required,
      ]),
      amountInILS: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(0.01),
      ]),
      date: new FormControl<Date | null>(null, [
        Validators.required,
        this.notFutureDateValidator(),
      ]),
      files: new FormControl<FileAnswer[]>([], [this.minFilesValidator(1)]),
      isValid: new FormControl<boolean>(true),
      clientCharge: new FormControl<boolean>(false),
      fullcharge: new FormControl<'full' | 'partial' | null>(null),
      clientChargeAmount: new FormControl<number | null>(0),
      approvedAmount: new FormControl<number | null>(null),
      commentAproval: new FormControl<string | null>(null),
    },
    {
      validators: [
        this.clientChargeAmountMaxValidator(),
        this.approvedAmountMaxValidator(),
      ],
    },
  );
  answerValueSig$ = toSignal(this.expense.valueChanges);
  registerOnChangeCallback: any = (expense: Expense) => {};
  onValidatorChange?: () => void;
  ngOnInit(): void {
    this.isNotUser$.subscribe((isNotUser) => {
      this.isUser = !isNotUser;
      this.applyExpensePermissions();
    });

    this.expense.controls.fullcharge.valueChanges.subscribe((type) => {
      if (type === 'full') {
        this.expense.controls.clientChargeAmount.setValue(
          this.expense.controls.amountInILS.value,
        );
      } else {
        this.expense.controls.clientChargeAmount.setValue(0);
      }
    });

    this.expense.valueChanges.subscribe(() => {
      const a = this.expense.getRawValue();
      console.log('expense.item.valueChanges', a);
      this.registerOnChangeCallback({
        id: this.currentExpense?.id ?? 0,
        answerId: this.currentExpense?.answerId ?? 0,
        amount: a.amountInILS,
        expenseType: a.expenseType?.guid ?? null,
        date: a.date ? new Date(a.date) : null,
        description: a.description ?? null,
        documentNumber: a.documentNumber ?? null,
        files: a.files ?? [],
        ModularRowNum: this.currentExpense?.ModularRowNum ?? this.row ?? 0,
        status: EAnswerStatus.Changed,
        clientCharge: a.clientCharge ?? false,
        fullcharge: a.fullcharge == 'full' ? true : false,
        clientChargeAmount: a.clientChargeAmount ?? 0,
        approvedAmount: a.approvedAmount ?? 0,
        commentAproval: a.commentAproval ?? null,
      } as Expense);
    });
    console.log('expense');

    this.expense.statusChanges.subscribe(() => {
      this.onValidatorChange?.();
    });
  }
  private applyExpensePermissions(): void {
    const regularControls = [
      this.expense.controls.expenseType,
      this.expense.controls.description,
      this.expense.controls.documentNumber,
      this.expense.controls.amountInILS,
      this.expense.controls.date,
      this.expense.controls.files,
    ];

    const clientChargeControls = [
      this.expense.controls.clientCharge,
      this.expense.controls.fullcharge,
      this.expense.controls.clientChargeAmount,
      this.expense.controls.approvedAmount,
      this.expense.controls.commentAproval,
    ];

    regularControls.forEach((control) => {
      if (this.isUser) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });

    clientChargeControls.forEach((control) => {
      if (this.canEditClientChargeFields()) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }
  private clientChargeAmountMaxValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const amountInILS = Number(control.get('amountInILS')?.value ?? 0);
      const clientChargeAmount = Number(
        control.get('clientChargeAmount')?.value ?? 0,
      );

      const clientCharge = control.get('clientCharge')?.value;

      if (!clientCharge) {
        return null;
      }

      if (clientChargeAmount > amountInILS) {
        return { clientChargeAmountGreaterThanAmount: true };
      }

      return null;
    };
  }

  private approvedAmountMaxValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const amountInILS = Number(control.get('amountInILS')?.value ?? 0);
      const approvedAmountValue = control.get('approvedAmount')?.value;

      if (approvedAmountValue === null || approvedAmountValue === undefined) {
        return null;
      }

      const approvedAmount = Number(approvedAmountValue);

      if (approvedAmount > amountInILS) {
        return { approvedAmountGreaterThanAmount: true };
      }

      return null;
    };
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.expense.valid ? null : { invalid: true };
  }
  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  writeValue(obj: Expense): void {
    this.currentExpense = obj ?? undefined;

    if (!obj) {
      return;
    }
    this.expense.patchValue(
      {
        amountInILS: obj.amount,
        expenseType:
          this.question?.optionsList?.answerOptionItem?.find(
            (o) => o.guid === obj.expenseType,
          ) ?? null,
        documentNumber: obj.documentNumber,
        files: obj.files,
        date: obj.date ? new Date(obj.date) : null,
        description: obj.description,
        clientCharge: obj.clientCharge ?? false,
        fullcharge:
          obj.fullcharge === true
            ? 'full'
            : obj.fullcharge === false
              ? 'partial'
              : null,
        clientChargeAmount: obj.clientChargeAmount ?? 0,
        approvedAmount: obj.approvedAmount ?? null,
        commentAproval: obj.commentAproval ?? null,
      },
      { emitEvent: false },
    );

    if (this.onValidatorChange) {
      this.onValidatorChange();
    }
    // this.expense.patchValue({ isValid: this.expense.valid })
    // this.selectedCurrency = currency;
    this.selectedTypeName = this.question?.optionsList?.answerOptionItem?.find(
      (o) => o.guid == obj.expenseType,
    )?.name;
    this.typeName = this.question?.optionsList?.name;
  }
  registerOnChange(fn: any): void {
    this.registerOnChangeCallback = fn;
  }
  registerOnTouched(fn: any): void {}
  // updateSelectionCurrency(op: string): void {
  //   if (!this.isUser) {
  //     return;
  //   }
  //   // this.expense.controls.currency.setValue(op);
  //   // this.isSelectCurrencyOpen = false;
  //   // this.selectedCurrency = op;

  //   if (op === '₪') {
  //     const amountInILS = Number(this.expense.controls.amountInILS.value);

  //     this.expense.controls.amount.setValue(amountInILS);
  //     this.expense.controls.exchangeRate.setValue(1);
  //   } else {
  //     this.expense.controls.amountInILS.setValue(null);
  //     this.expense.controls.exchangeRate.setValue(null);
  //   }
  //   this.expense.markAsTouched();
  //   this.expense.updateValueAndValidity();
  // }
  updateSelectionType(op: AnswerOptionItem) {
    if (!this.isUser) {
      return;
    }
    this.expense.controls.expenseType.setValue(op);
    this.selectedTypeName = op.name;
    this.isSelectTypeOpen = false;
  }
  chooseDate(date: ICalendarResults) {
    if (!this.isUser) {
      return;
    }
    if (!date?.startDate) {
      return;
    }

    const selectedDate = new Date(date.startDate);
    const today = new Date();

    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      return;
    }

    this.expense.controls.date.setValue(date.startDate ?? null);
    this.selectedDateIsOpened = false;
  }
  openPopupDate() {
    if (!this.isUser) {
      return;
    }
    const triggerRect = this.trigger.nativeElement.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    if (triggerRect.bottom + 400 > viewportHeight) {
      this.positonAreaForSelectedDate[0].overlayY = 'bottom';
    } else {
      this.positonAreaForSelectedDate[0].overlayY = 'top';
    }
    this.selectedDateIsOpened = true;
  }

  deleteExpenseSun() {
    if (!this.isUser) {
      return;
    }
    this.deleteExpense.emit();
  }

  private notFutureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const selectedDate = new Date(control.value);
      const today = new Date();

      selectedDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      return selectedDate > today ? { futureDate: true } : null;
    };
  }

  private minFilesValidator(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const files = control.value as FileAnswer[] | null;
      return files && files.length >= min ? null : { minFiles: true };
    };
  }

  // private amountInIlsValidator(): ValidatorFn {
  //   return (group: AbstractControl): ValidationErrors | null => {
  //     const amount = Number(group.get('amount')?.value ?? 0);
  //     // const rate = Number(group.get('exchangeRate')?.value ?? 0);
  //     const amountInILS = Number(group.get('amountInILS')?.value ?? 0);
  //     // const currency = group.get('currency')?.value;

  //     // if (currency === '₪') {
  //     //   return amountInILS === amount ? null : { amountInILSNotMatch: true };
  //     // }

  //     // const expected = Number((amount * rate).toFixed(2));
  //     const actual = Number(amountInILS.toFixed(2));

  //     return actual;
  //   };
  // }

  private readonly language = environment.lang ?? 'he';

  private readonly messages = {
    he: {
      expenseTypeRequired: 'יש לבחור סוג הוצאה',
      // currencyRequired: 'יש לבחור סוג מטבע',
      amountRequired: 'יש להזין סכום',
      amountMin: 'הסכום חייב להיות גדול מ־0',
      amountInILSRequired: 'יש להזין סכום בש״ח',
      amountInILSMin: 'הסכום בש״ח חייב להיות גדול מ־0',
      // exchangeRateRequired: 'יש להזין שער מטבע',
      // exchangeRateMin: 'שער המטבע חייב להיות גדול מ־0',
      dateRequired: 'יש לבחור תאריך',
      futureDate: 'התאריך לא יכול להיות עתידי',
      filesMin: 'יש לצרף לפחות קובץ אחד',
      amountInILSNotMatch: 'הסכום בש״ח חייב להיות שווה לסכום כפול שער המטבע',
      fullchargeRequired: 'יש לבחור סוג חיוב',
      clientChargeAmountGreaterThanAmount:
        'סכום חיוב לקוח לא יכול להיות גדול מסכום ההוצאה',
      approvedAmountGreaterThanAmount:
        'הסכום המאושר לא יכול להיות גדול מהסכום שהתבקש',
    },
    en: {
      expenseTypeRequired: 'Please select an expense type',
      // currencyRequired: 'Please select a currency',
      amountRequired: 'Please enter an amount',
      amountMin: 'The amount must be greater than 0',
      amountInILSRequired: 'Please enter the amount in ILS',
      amountInILSMin: 'The amount in ILS must be greater than 0',
      // exchangeRateRequired: 'Please enter an exchange rate',
      // exchangeRateMin: 'The exchange rate must be greater than 0',
      dateRequired: 'Please select a date',
      futureDate: 'The date cannot be in the future',
      filesMin: 'Please attach at least one file',
      amountInILSNotMatch:
        'The amount in ILS must equal the amount multiplied by the exchange rate',
      fullchargeRequired: 'Please select a charge type',
      clientChargeAmountGreaterThanAmount:
        'Client charge amount cannot be greater than the expense amount',
      approvedAmountGreaterThanAmount:
        'The approved amount cannot be greater than the requested amount',
    },
  };

  private get t() {
    return this.language === 'en' ? this.messages.en : this.messages.he;
  }
  get expenseErrorMessage(): string | null {
    if (!this.expense.touched && !this.expense.dirty) {
      return null;
    }

    if (this.expense.controls.expenseType.hasError('required')) {
      return this.t.expenseTypeRequired;
    }

    // if (this.expense.controls.currency.hasError('required')) {
    //   return this.t.currencyRequired;
    // }

    // if (this.expense.controls.amount.hasError('required')) {
    //   return this.t.amountRequired;
    // }

    // if (this.expense.controls.amount.hasError('min')) {
    //   return this.t.amountMin;
    // }

    if (this.expense.controls.amountInILS.hasError('required')) {
      return this.t.amountInILSRequired;
    }

    if (this.expense.controls.amountInILS.hasError('min')) {
      return this.t.amountInILSMin;
    }

    // if (this.expense.controls.exchangeRate.hasError('required')) {
    //   return this.t.exchangeRateRequired;
    // }

    // if (this.expense.controls.exchangeRate.hasError('min')) {
    //   return this.t.exchangeRateMin;
    // }

    if (this.expense.controls.date.hasError('required')) {
      return this.t.dateRequired;
    }

    if (this.expense.controls.date.hasError('futureDate')) {
      return this.t.futureDate;
    }

    if (this.expense.controls.files.hasError('minFiles')) {
      return this.t.filesMin;
    }

    if (this.expense.hasError('amountInILSNotMatch')) {
      return this.t.amountInILSNotMatch;
    }
    if (this.expense.hasError('clientChargeAmountGreaterThanAmount')) {
      return this.t.clientChargeAmountGreaterThanAmount;
    }

    if (this.expense.hasError('approvedAmountGreaterThanAmount')) {
      return this.t.approvedAmountGreaterThanAmount;
    }
    return null;
  }

  // private updateAmountInILSForForeignCurrency(): void {
  //   if (!this.isUser) {
  //     return;
  //   }

  //   const amount = this.expense.controls.amount.value;
  //   const exchangeRate = this.expense.controls.exchangeRate.value;

  //   if (amount == null || exchangeRate == null || exchangeRate <= 0) {
  //     this.expense.controls.amountInILS.setValue(null);
  //     return;
  //   }

  //   const amountInILS = Number((amount * exchangeRate).toFixed(2));

  //   this.expense.controls.amountInILS.setValue(amountInILS);
  // }

  setAmountInILS(event: Event): void {
    if (!this.isUser) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const value = input.value === '' ? null : Number(input.value);

    this.expense.controls.amountInILS.setValue(value);
    this.expense.controls.amountInILS.markAsTouched();

    // if (this.expense.controls.currency.value === '₪') {
    //   this.expense.controls.amount.setValue(value);
    //   this.expense.controls.exchangeRate.setValue(1);
    // }
    this.expense.markAsTouched();
    this.expense.updateValueAndValidity();
  }

  setClientChargeAmount(event: Event): void {
    if (
      !this.canEditClientChargeFields() &&
      this.expense.controls.fullcharge.value === 'full'
    ) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const value = input.value === '' ? null : Number(input.value);

    this.expense.controls.clientChargeAmount.setValue(value);
    this.expense.controls.clientChargeAmount.markAsTouched();

    this.expense.markAsTouched();
    this.expense.updateValueAndValidity();
  }

  setApprovedAmount(event: Event): void {
    if (!this.canEditClientChargeFields()) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const value = input.value === '' ? null : Number(input.value);

    this.expense.controls.approvedAmount.setValue(value);
    this.expense.controls.approvedAmount.markAsTouched();

    this.expense.markAsTouched();
    this.expense.updateValueAndValidity();
  }

  setAmount(event: Event): void {
    if (!this.isUser) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const value = input.value === '' ? null : Number(input.value);

    // this.expense.controls.amount.setValue(value);
    // this.expense.controls.amount.markAsTouched();

    // if (this.expense.controls.currency.value === '₪') {
    //   this.expense.controls.amountInILS.setValue(value);
    //   this.expense.controls.exchangeRate.setValue(1);
    // } else {
    // this.updateAmountInILSForForeignCurrency();
    // }
    // this.expense.markAsTouched();
    this.expense.updateValueAndValidity();
  }

  // setExchangeRate(event: Event): void {
  //   if (!this.isUser) {
  //     return;
  //   }
  //   const input = event.target as HTMLInputElement;
  //   const value = input.value === '' ? 0 : Number(input.value);

  //   // this.expense.controls.exchangeRate.setValue(value);
  //   // this.expense.controls.exchangeRate.markAsTouched();

  //   // if (this.expense.controls.currency.value !== '₪') {
  //   //   this.updateAmountInILSForForeignCurrency();
  //   // }
  //   // this.expense.markAsTouched();
  //   this.expense.updateValueAndValidity();
  // }
}
